// Retrieval map — one vector query at a time, drawn as an instrument: the
// query is the star at the center, retrieved chunks orbit it (closer = higher
// similarity), angle groups hits by source document. Bright hits were PACKED
// into the model's prompt; gray ones were retrieved but not packed — the
// trust distinction. A chip strip switches between the run's queries; during
// live streaming the newest query is shown as it happens.
// Pure SVG — deterministic from the event slice, so playback scrubbing works.
import { useMemo, useState } from 'react';

const GOLDEN = Math.PI * (3 - Math.sqrt(5));

// Modes that never touch vector search get an honest explanation instead of
// an empty starfield — an empty pane reads as a bug.
const MODE_NO_VECTORS = {
  ingest: 'Ingest parses and versions the document — vector indexing happens in the project-brain task afterwards.',
  graph: 'Graph builds embed clauses for similarity edges but run no retrieval queries.',
};

// `packed` is the current flag (in the prompt for that call); older traces
// used `used` — read both so replays of old runs stay correct.
const isPacked = (h) => (h.packed ?? h.used) !== false;

export default function RetrievalMap({ events, mode, eventCounts, height = 300 }) {
  // Group hits under the query that produced them (stream order).
  const queries = useMemo(() => {
    const out = [];
    for (const e of events) {
      if (e.type === 'vector.query') out.push({ q: e, hits: [] });
      else if (e.type === 'vector.hit' && out.length) out[out.length - 1].hits.push(e);
    }
    return out;
  }, [events]);
  // Echo-chamber guard telemetry: hits the recall filter dropped (prior
  // AI-derived drafts of the same project) — surfaced, never silent.
  const excluded = useMemo(
    () => events.filter((e) => e.type === 'vector.exclude').reduce((s, e) => s + (e.excluded || 0), 0),
    [events]
  );

  const [selected, setSelected] = useState(null);
  const idx = selected != null && selected < queries.length ? selected : queries.length - 1;
  const active = queries[idx];

  const W = 560;
  const H = height;
  const cx = W / 2;
  const cy = H / 2;
  const Rmax = Math.min(W, H) / 2 - 26;

  const { placed, groups } = useMemo(() => {
    if (!active) return { placed: [], groups: [] };
    const hits = active.hits;
    const scores = hits.map((h) => h.score ?? 0);
    const max = Math.max(...scores, 0.0001);
    const min = Math.min(...scores, max);
    // Angle: group by source doc so clusters read as documents.
    const docOrder = new Map();
    for (const h of hits) {
      const k = h.doc || h.deal || '?';
      if (!docOrder.has(k)) docOrder.set(k, docOrder.size);
    }
    const seen = new Map();
    const pts = hits.map((h) => {
      const k = h.doc || h.deal || '?';
      const nth = seen.get(k) || 0;
      seen.set(k, nth + 1);
      const baseAngle = docOrder.get(k) * GOLDEN;
      const a = baseAngle + (nth - 1.5) * 0.17;
      const rel = max === min ? 0.5 : (max - (h.score ?? 0)) / (max - min);
      const r = 44 + rel * (Rmax - 44);
      return { h, k, x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r, w: 0.6 + 2.2 * ((h.score ?? 0) / max) };
    });
    // One label per source document, pushed outward from the cluster centroid.
    const byDoc = new Map();
    for (const p of pts) {
      if (!byDoc.has(p.k)) byDoc.set(p.k, []);
      byDoc.get(p.k).push(p);
    }
    const gs = [...byDoc.entries()].map(([k, ps]) => {
      const mx = ps.reduce((s, p) => s + p.x, 0) / ps.length;
      const my = ps.reduce((s, p) => s + p.y, 0) / ps.length;
      const dx = mx - cx;
      const dy = my - cy;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const push = 22;
      return { k, n: ps.length, x: mx + (dx / d) * push, y: my + (dy / d) * push };
    });
    return { placed: pts, groups: gs };
  }, [active, cx, cy, Rmax]);

  return (
    <div>
      {queries.length > 1 && (
        <div className="cockpit-qchips">
          {queries.map((q, i) => (
            <button
              key={`${q.q.at}-${i}`}
              type="button"
              className={`cockpit-qchip ${i === idx ? 'is-selected' : ''}`}
              title={q.q.query}
              onClick={() => setSelected(i)}
            >
              {i + 1}. {q.q.query}
            </button>
          ))}
        </div>
      )}
      <div className="cockpit-canvas" style={{ height }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          {active && (
            <g>
              {/* distance rings */}
              {[0.33, 0.66, 1].map((f) => (
                <circle key={f} cx={cx} cy={cy} r={44 + f * (Rmax - 44)} fill="none" stroke="#1b2941" strokeWidth="1" />
              ))}
              {/* hit rays + dots */}
              {placed.map(({ h, x, y, w }, i) => (
                <g key={`${h.at}-${i}`} opacity={isPacked(h) ? 1 : 0.45}>
                  <line x1={cx} y1={cy} x2={x} y2={y} stroke={isPacked(h) ? '#2f6fed' : '#3a4a60'} strokeWidth={w} />
                  <circle cx={x} cy={y} r={isPacked(h) ? 5.5 : 4} fill={isPacked(h) ? '#6ea8ff' : '#5a6c82'}>
                    <title>
                      {`${h.doc || h.deal || h.key || ''}${h.heading ? ` — ${h.heading}` : ''}\nscore ${h.score ?? '—'} · ${isPacked(h) ? 'packed into the prompt' : 'retrieved, not packed'}`}
                    </title>
                  </circle>
                </g>
              ))}
              {/* source-document cluster labels */}
              {groups.map((g) => (
                <text key={g.k} x={g.x} y={g.y} textAnchor="middle" fill="#7d93b0" fontSize="8">
                  {String(g.k).length > 26 ? `${String(g.k).slice(0, 25)}…` : g.k} ({g.n})
                </text>
              ))}
              {/* the query star */}
              <circle cx={cx} cy={cy} r={9} fill="#e0a414" />
              <circle cx={cx} cy={cy} r={14} fill="none" stroke="#e0a414" strokeWidth="1" opacity="0.5" />
              <text x={cx} y={cy - 18} textAnchor="middle" fill="#c9d6e8" fontSize="9">
                {String(active.q.query || '').slice(0, 70)}
              </text>
              {/* legend + engine */}
              <text x={10} y={H - 24} fill="#6ea8ff" fontSize="8">● packed into the prompt</text>
              <text x={10} y={H - 12} fill="#5a6c82" fontSize="8">● retrieved, not packed</text>
              {excluded > 0 && (
                <text x={10} y={12} fill="#e0a414" fontSize="8">
                  ⚠ {excluded} hit{excluded === 1 ? '' : 's'} from prior derived drafts excluded (echo-chamber guard)
                </text>
              )}
              <text x={W - 10} y={H - 12} textAnchor="end" fill="#5a6c82" fontSize="8">
                {active.q.engine || 'vector'} · topK {active.q.topK || '—'}{active.q.scope ? ` · ${active.q.scope}` : ''}
              </text>
            </g>
          )}
        </svg>
        {!active && (
          <div className="cockpit-canvas-empty">
            {MODE_NO_VECTORS[mode] ? (
              <span>
                {MODE_NO_VECTORS[mode]}
                {(eventCounts?.['graph.hit'] || eventCounts?.['graph.lookup']) && (
                  <>
                    <br />
                    <span style={{ opacity: 0.8 }}>
                      {(eventCounts['graph.hit'] || 0).toLocaleString()} graph hits from{' '}
                      {(eventCounts['graph.lookup'] || 0).toLocaleString()} lookup{(eventCounts['graph.lookup'] || 0) === 1 ? '' : 's'} — see the graph pane above.
                    </span>
                  </>
                )}
              </span>
            ) : (
              'Vector queries appear here as the agent retrieves — the star is the query, orbiting dots are the chunks it pulled (closer = more similar; bright = actually used).'
            )}
          </div>
        )}
      </div>
    </div>
  );
}
